<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { UiDialog, UiText, UiSelect, UiInputField } from '@shopexperts/shared';
import type { ExpertTypeInput, ListingType } from '@shopexperts/legacy-api';
import { useExpertTypeSwitch } from '@/app/composables/expert-manager/useExpertTypeSwitch';

const props = defineProps<{
  expertId: number;
  expertName: string;
  currentListingType: ListingType;
}>();

const open = defineModel<boolean>({ default: false });

const { switchExpertType } = useExpertTypeSwitch();

const targetTypeOptions = computed(() => {
  const all: { label: string; value: ExpertTypeInput }[] = [
    { label: 'Freelancer', value: 'freelancer' },
    { label: 'Agency', value: 'agency' },
    { label: 'App Service Provider', value: 'app_service_provider' },
  ];
  return all.filter((o) => o.value !== props.currentListingType);
});

const targetType = ref<ExpertTypeInput | ''>('');

const agencyName = ref('');
const representativeName = ref('');
const representativeRole = ref('');
const representativeLinkedin = ref('');
const appTier = ref<'built_for_shopify' | ''>('');

const firstName = ref('');
const lastName = ref('');
const role = ref('');

const isAgencyTarget = computed(() =>
  targetType.value === 'agency' || targetType.value === 'app_service_provider',
);
const isAppServiceTarget = computed(() => targetType.value === 'app_service_provider');
const isFreelancerTarget = computed(() => targetType.value === 'freelancer');

watch(open, (v) => {
  if (v) {
    targetType.value = '';
    agencyName.value = '';
    representativeName.value = '';
    representativeRole.value = '';
    representativeLinkedin.value = '';
    appTier.value = '';
    firstName.value = '';
    lastName.value = '';
    role.value = '';
  }
});

const canSubmit = computed(() => !!targetType.value);

const isLoading = ref(false);

async function handleConfirm() {
  if (!targetType.value) return;
  isLoading.value = true;
  try {
    const body: Record<string, unknown> = {
      expert_type: targetType.value,
    };

    if (isAgencyTarget.value) {
      if (agencyName.value) body.agency_name = agencyName.value;
      if (representativeName.value) body.representative_name = representativeName.value;
      if (representativeRole.value) body.representative_role = representativeRole.value;
      if (representativeLinkedin.value) body.representative_linkedin = representativeLinkedin.value;
      if (isAppServiceTarget.value && appTier.value) {
        body.agency_type_fields = { app_tier: appTier.value };
      }
    }

    if (isFreelancerTarget.value) {
      if (firstName.value) body.first_name = firstName.value;
      if (lastName.value) body.last_name = lastName.value;
      if (role.value) body.role = role.value;
    }

    await switchExpertType.mutateAsync({
      expertId: props.expertId,
      body: body as any,
    });
    open.value = false;
  } finally {
    isLoading.value = false;
  }
}

const appTierOptions = [
  { label: 'Built for Shopify', value: 'built_for_shopify' },
];
</script>

<template>
  <UiDialog
    v-model="open"
    title="Switch Expert Type"
    primaryActionLabel="Switch Type"
    secondaryActionLabel="Cancel"
    size="sm"
    :loading="isLoading"
    @primaryAction="handleConfirm"
    @secondaryAction="open = false"
    @close="open = false"
  >
    <div class="space-y-4">
      <div class="px-1 py-2 rounded-md bg-gray-50 border border-gray-100">
        <UiText as="span" variant="caption-normal" class="text-gray-500">
          Switching type for
          <span class="font-medium text-gray-800">{{ expertName }}</span>
        </UiText>
      </div>

      <div>
        <UiText as="label" variant="caption-normal" class="text-gray-600 block mb-1.5">
          Target Type *
        </UiText>
        <UiSelect
          v-model="targetType"
          :options="targetTypeOptions"
          placeholder="Select target type..."
          size="md"
        />
      </div>

      <!-- Agency / App Service Provider fields -->
      <template v-if="isAgencyTarget">
        <UiText as="p" variant="caption-normal" class="text-gray-400 pt-1">
          Leave fields empty to auto-derive from the existing profile.
        </UiText>

        <UiInputField
          v-model="agencyName"
          placeholder="Agency name"
          size="md"
        />
        <UiInputField
          v-model="representativeName"
          placeholder="Representative name"
          size="md"
        />
        <UiInputField
          v-model="representativeRole"
          placeholder="Representative role"
          size="md"
        />
        <UiInputField
          v-model="representativeLinkedin"
          placeholder="Representative LinkedIn URL"
          size="md"
        />

        <div v-if="isAppServiceTarget">
          <UiText as="label" variant="caption-normal" class="text-gray-600 block mb-1.5">
            App Tier
          </UiText>
          <UiSelect
            v-model="appTier"
            :options="appTierOptions"
            placeholder="Select app tier..."
            size="md"
          />
        </div>
      </template>

      <!-- Freelancer fields -->
      <template v-if="isFreelancerTarget">
        <UiText as="p" variant="caption-normal" class="text-gray-400 pt-1">
          Leave fields empty to auto-derive from the existing profile.
        </UiText>

        <UiInputField
          v-model="firstName"
          placeholder="First name"
          size="md"
        />
        <UiInputField
          v-model="lastName"
          placeholder="Last name"
          size="md"
        />
        <UiInputField
          v-model="role"
          placeholder="Role"
          size="md"
        />
      </template>
    </div>
  </UiDialog>
</template>
